import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../Header';
import Footer from '../Footer';
import { getOrgBySlug, getOrgById, getMaterialsForOrg, getIncidentsForOrg } from './libraryData';
import { getRolesForOrg } from './roleMap';
import { useOrgs } from '../../hooks/useLibraryData';
import { useAdmin } from '../../contexts/AdminContext';
import EditableText from '../admin/EditableText';
import SourcesEditor from '../admin/SourcesEditor';
import TagsEditor from '../admin/TagsEditor';
import './Library.css';

function slugify(text) {
  return text.toLowerCase().replace(/['']/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function OrgDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const { orgs: allOrgsData, refetch: refetchOrgs } = useOrgs();
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editedData, setEditedData] = useState({});
  const [showSourcesEditor, setShowSourcesEditor] = useState(false);
  const [showTagsEditor, setShowTagsEditor] = useState(false);
  const [showParentOrgPicker, setShowParentOrgPicker] = useState(false);
  const [parentOrgSearch, setParentOrgSearch] = useState('');
  const [showAffiliatePicker, setShowAffiliatePicker] = useState(false);
  const [affiliateSearch, setAffiliateSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await fetch(`/api/orgs/${slug}`);
        if (res.ok) {
          const data = await res.json();
          // Parse JSON string fields
          if (typeof data.sources === 'string') data.sources = JSON.parse(data.sources);
          if (typeof data.tags === 'string') data.tags = JSON.parse(data.tags);
          if (typeof data.roles === 'string') data.roles = JSON.parse(data.roles);
          if (!cancelled) setApiData(data);
        }
      } catch (e) {
        console.error('Failed to fetch from API:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [slug]);

  // Use API data if available, fall back to static
  const staticOrg = getOrgBySlug(slug);
  const org = apiData || staticOrg;


  const saveToAPI = async (field, value) => {
    try {
      const response = await fetch(`/api/orgs/${org.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: value }),
      });
      if (!response.ok) {
        const err = await response.json();
        console.error('Save failed:', err);
        alert('Save failed: ' + (err.error || 'Unknown error'));
        return false;
      }
      return true;
    } catch (error) {
      console.error('Save error:', error);
      alert('Save failed: network error');
      return false;
    }
  };

  const handleFieldSave = async (field, value) => {
    if (field === 'name') {
      const newSlug = slugify(value);
      try {
        const response = await fetch(`/api/orgs/${org.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: value, slug: newSlug }),
        });
        if (response.ok) {
          setEditedData(prev => ({ ...prev, name: value, slug: newSlug }));
          if (newSlug !== slug) navigate(`/organisations/${newSlug}`, { replace: true });
        } else {
          const err = await response.json();
          alert('Save failed: ' + (err.error || 'Unknown error'));
        }
      } catch (error) {
        alert('Save failed: network error');
      }
      return;
    }
    const ok = await saveToAPI(field, value);
    if (ok) setEditedData(prev => ({ ...prev, [field]: value }));
  };

  // Add/remove affiliates by updating the OTHER org's parentOrg field
  const addAffiliate = async (childOrgId) => {
    try {
      const response = await fetch(`/api/orgs/${childOrgId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ parentOrg: org.id }),
      });
      if (response.ok) {
        setShowAffiliatePicker(false);
        setAffiliateSearch('');
        await refetchOrgs();
      } else {
        const err = await response.json();
        alert('Failed to add affiliate: ' + (err.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Failed to add affiliate: network error');
    }
  };

  const removeAffiliate = async (childOrgId) => {
    try {
      const response = await fetch(`/api/orgs/${childOrgId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ parentOrg: null }),
      });
      if (response.ok) {
        await refetchOrgs();
      } else {
        const err = await response.json();
        alert('Failed to remove affiliate: ' + (err.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Failed to remove affiliate: network error');
    }
  };

  const handleSourcesSave = async (sources) => {
    const ok = await saveToAPI('sources', sources);
    if (ok) setEditedData(prev => ({ ...prev, sources }));
    setShowSourcesEditor(false);
  };

  const handleTagsSave = async (tags) => {
    const ok = await saveToAPI('tags', tags);
    if (ok) setEditedData(prev => ({ ...prev, tags }));
    setShowTagsEditor(false);
  };

  if (loading) {
    return (
      <>
        <Header />
        <main className='detail-page-container'>
          <div className='detail-content'>
            <p>Loading...</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!org) {
    return (
      <>
        <Header />
        <main className='detail-page-container'>
          <div className='detail-content'>
            <h1>Organisation not found</h1>
            <p>The organisation you are looking for does not exist.</p>
            <Link to='/library'>Back to Library</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const materials = getMaterialsForOrg(org.id);
  const incidents = getIncidentsForOrg(org.id);
  const currentParentOrgId = editedData.parentOrg !== undefined ? editedData.parentOrg : org.parentOrg;
  const parentOrg = currentParentOrgId ? allOrgsData.find(o => o.id === currentParentOrgId) || getOrgById(currentParentOrgId) : null;
  const childOrgs = allOrgsData.filter(o => o.parentOrg === org.id);
  const pageTitle = `${org.name} - Organisations - Hasbara Tracker`;
  const pageDescription = org.description.substring(0, 160);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <link rel='canonical' href={`https://hasbaratracker.com/organisations/${slug}`} />
        <meta name='description' content={pageDescription} />
        <meta property='og:url' content={`https://hasbaratracker.com/organisations/${slug}`} />
        <meta property='og:title' content={`${org.name} - Hasbara Tracker`} />
        <meta property='og:description' content={pageDescription} />
        <meta property='og:type' content='article' />
        <meta property='og:site_name' content='Hasbara Tracker' />
        <meta property='og:image' content='https://files.hasbaratracker.com/htlogo_twittercard.jpg' />
        <meta name='twitter:card' content='summary_large_image' />
        <meta name='twitter:site' content='@hasbaratracker' />
        <meta name='twitter:title' content={`${org.name} - Hasbara Tracker`} />
        <meta name='twitter:description' content={pageDescription} />
        <meta name='twitter:image' content='https://files.hasbaratracker.com/htlogo_twittercard.jpg' />
      </Helmet>

      <Header />

      <main className='detail-page-container'>
        {/* Breadcrumb */}
        <nav className='breadcrumb' aria-label='Breadcrumb'>
          <Link to='/library'>Library</Link>
          <span>/</span>
          <Link to='/library' state={{ tab: 'organisations' }}>Organisations</Link>
          <span>/</span>
          <span>{org.name}</span>
        </nav>

        {/* Name */}
        <h1 className='detail-title'>
          {isAdmin ? (
            <EditableText
              value={editedData.name !== undefined ? editedData.name : org.name}
              onSave={(val) => handleFieldSave('name', val)}
              tag='span'
            />
          ) : (
            org.name
          )}
          {org.acronym && <span className='detail-acronym'> ({org.acronym})</span>}
        </h1>

        {/* Two-column layout */}
        <div className='detail-layout'>
          {/* Main column */}
          <div className='detail-main'>
            {/* Description */}
            <section className='detail-description'>
              <p className='org-full-description'>
                {isAdmin ? (
                  <EditableText
                    value={editedData.description !== undefined ? editedData.description : org.description}
                    onSave={(val) => handleFieldSave('description', val)}
                    tag='span'
                    multiline={true}
                  />
                ) : (
                  org.description
                )}
              </p>
            </section>

            {/* Materials */}
            {materials && materials.length > 0 && (
              <section className='detail-section'>
                <h2 className='detail-section-heading'>Materials produced</h2>
                <div className='related-materials-list'>
                  {materials.map((material, idx) => (
                    <article key={idx} className='related-material-item'>
                      <div className='material-item-header'>
                        <span className='material-type-badge'>{material.type.toUpperCase()}</span>
                        <h3>
                          <Link to={`/materials/${material.slug}`}>
                            {material.title}
                          </Link>
                        </h3>
                      </div>
                      {material.date && (
                        <span className='material-item-date'>{material.date}</span>
                      )}
                    </article>
                  ))}
                </div>
              </section>
            )}

            {/* Linked incidents */}
            {incidents && incidents.length > 0 && (
              <section className='detail-section'>
                <h2 className='detail-section-heading'>Linked incidents</h2>
                <div className='related-incidents-list'>
                  {incidents.map((incident, idx) => (
                    <article key={idx} className='related-incident-item'>
                      <div className='incident-item-header'>
                        {incident.date && (
                          <span className='incident-date'>{incident.date}</span>
                        )}
                        <h3>
                          <Link to={`/incidents/${incident.slug}`}>
                            {incident.title}
                          </Link>
                        </h3>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <aside className='detail-sidebar'>
            <dl className='detail-meta-list'>
              {(org.country || isAdmin) && (
                <>
                  <dt>Location</dt>
                  <dd>
                    {isAdmin ? (
                      <EditableText
                        value={editedData.country !== undefined ? editedData.country : org.country || ''}
                        onSave={(val) => handleFieldSave('country', val)}
                        tag='span'
                        placeholder='Add location...'
                      />
                    ) : (
                      org.country
                    )}
                  </dd>
                </>
              )}

              {(org.scope || isAdmin) && (
                <>
                  <dt>Scope</dt>
                  <dd>
                    {isAdmin ? (
                      <select
                        className='admin-input'
                        value={editedData.scope !== undefined ? editedData.scope : org.scope || ''}
                        onChange={(e) => handleFieldSave('scope', e.target.value || null)}
                        style={{fontSize: 'inherit', padding: '2px 4px'}}
                      >
                        <option value=''>Select scope...</option>
                        <option value='Local'>Local</option>
                        <option value='National'>National</option>
                        <option value='International'>International</option>
                        <option value='Intergovernmental'>Intergovernmental</option>
                      </select>
                    ) : (
                      org.scope
                    )}
                  </dd>
                </>
              )}

              {getRolesForOrg(org.id, org.roles).length > 0 && (
                <>
                  <dt>Roles</dt>
                  <dd>
                    <div className='library-org-domains'>
                      {getRolesForOrg(org.id, org.roles).map((role, idx) => (
                        <React.Fragment key={role}>
                          {idx > 0 && <span className='library-org-domain-sep'> / </span>}
                          <span className='library-org-domain'>{role}</span>
                        </React.Fragment>
                      ))}
                    </div>
                  </dd>
                </>
              )}

              {(org.founded || isAdmin) && (
                <>
                  <dt>Founded</dt>
                  <dd>
                    {isAdmin ? (
                      <EditableText
                        value={editedData.founded !== undefined ? editedData.founded : org.founded || ''}
                        onSave={(val) => handleFieldSave('founded', val)}
                        tag='span'
                        placeholder='Add year...'
                      />
                    ) : (
                      org.founded
                    )}
                  </dd>
                </>
              )}

              {(org.website || isAdmin) && (
                <>
                  <dt>Website</dt>
                  <dd>
                    {isAdmin ? (
                      <EditableText
                        value={editedData.website !== undefined ? editedData.website : org.website || ''}
                        onSave={(val) => handleFieldSave('website', val)}
                        tag='span'
                        placeholder='Add website URL...'
                      />
                    ) : (
                      <a
                        href={org.archivedWebsite || org.website}
                        target='_blank'
                        rel='noopener noreferrer nofollow'
                        className='detail-website-link'
                      >
                        {org.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/.*$/, '')}
                        {org.archivedWebsite && <span className='detail-archived-badge'> (archived)</span>}
                      </a>
                    )}
                  </dd>
                </>
              )}
            </dl>

            {/* Parent org */}
            {(parentOrg || isAdmin) && (
              <div className='detail-sidebar-section'>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <h3 className='detail-sidebar-heading'>Part of</h3>
                  {isAdmin && (
                    <button
                      className='admin-btn-small'
                      onClick={() => setShowParentOrgPicker(!showParentOrgPicker)}
                      title='Edit parent org'
                      style={{padding: '0.25rem 0.5rem', fontSize: '0.85rem'}}
                    >
                      {showParentOrgPicker ? 'Cancel' : 'Edit'}
                    </button>
                  )}
                </div>
                {showParentOrgPicker ? (
                  <div className='admin-org-picker'>
                    <input
                      type='text'
                      className='admin-edit-input'
                      placeholder='Search organisations...'
                      value={parentOrgSearch}
                      onChange={(e) => setParentOrgSearch(e.target.value)}
                      autoFocus
                    />
                    <ul className='admin-org-picker-list'>
                      {currentParentOrgId && (
                        <li>
                          <button
                            className='admin-org-picker-item admin-org-picker-remove'
                            onClick={async () => {
                              const ok = await saveToAPI('parentOrg', null);
                              if (ok) {
                                setEditedData(prev => ({ ...prev, parentOrg: null }));
                                setShowParentOrgPicker(false);
                                setParentOrgSearch('');
                              }
                            }}
                          >
                            Remove parent org
                          </button>
                        </li>
                      )}
                      {allOrgsData
                        .filter(o => o.id !== org.id && o.name.toLowerCase().includes(parentOrgSearch.toLowerCase()))
                        .slice(0, 10)
                        .map(o => (
                          <li key={o.id}>
                            <button
                              className={`admin-org-picker-item ${o.id === currentParentOrgId ? 'active' : ''}`}
                              onClick={async () => {
                                const ok = await saveToAPI('parentOrg', o.id);
                                if (ok) {
                                  setEditedData(prev => ({ ...prev, parentOrg: o.id }));
                                  setShowParentOrgPicker(false);
                                  setParentOrgSearch('');
                                }
                              }}
                            >
                              {o.name}
                            </button>
                          </li>
                        ))}
                    </ul>
                  </div>
                ) : (
                  <ul className='detail-sidebar-links'>
                    {parentOrg ? (
                      <li>
                        <Link to={`/organisations/${parentOrg.slug}`}>
                          {parentOrg.name}
                        </Link>
                      </li>
                    ) : isAdmin ? (
                      <li className='admin-placeholder'>No parent org set</li>
                    ) : null}
                  </ul>
                )}
              </div>
            )}

            {/* Child / affiliated orgs */}
            {(childOrgs.length > 0 || isAdmin) && (
              <div className='detail-sidebar-section'>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <h3 className='detail-sidebar-heading'>Affiliates</h3>
                  {isAdmin && (
                    <button
                      className='admin-btn-small'
                      onClick={() => setShowAffiliatePicker(!showAffiliatePicker)}
                      title='Add or remove affiliates'
                      style={{padding: '0.25rem 0.5rem', fontSize: '0.85rem'}}
                    >
                      {showAffiliatePicker ? 'Done' : 'Edit'}
                    </button>
                  )}
                </div>
                <ul className='detail-sidebar-links'>
                  {childOrgs.map((child) => (
                    <li key={child.id} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <Link to={`/organisations/${child.slug}`}>
                        {child.name}
                      </Link>
                      {isAdmin && showAffiliatePicker && (
                        <button
                          className='admin-btn-remove'
                          onClick={() => removeAffiliate(child.id)}
                          title={`Remove ${child.name} as affiliate`}
                          style={{marginLeft: '8px', padding: '2px 6px', fontSize: '12px'}}
                        >
                          ×
                        </button>
                      )}
                    </li>
                  ))}
                  {childOrgs.length === 0 && !showAffiliatePicker && isAdmin && (
                    <li className='admin-placeholder'>No affiliates</li>
                  )}
                </ul>
                {showAffiliatePicker && (
                  <div className='admin-org-picker' style={{marginTop: '8px'}}>
                    <input
                      type='text'
                      className='admin-edit-input'
                      placeholder='Search to add affiliate...'
                      value={affiliateSearch}
                      onChange={(e) => setAffiliateSearch(e.target.value)}
                      autoFocus
                    />
                    <ul className='admin-org-picker-list'>
                      {allOrgsData
                        .filter(o =>
                          o.id !== org.id &&
                          o.parentOrg !== org.id &&
                          affiliateSearch.trim() !== '' &&
                          o.name.toLowerCase().includes(affiliateSearch.toLowerCase())
                        )
                        .slice(0, 10)
                        .map(o => (
                          <li key={o.id}>
                            <button
                              className='admin-org-picker-item'
                              onClick={() => addAffiliate(o.id)}
                            >
                              {o.name}
                              {o.parentOrg && (
                                <span style={{color: '#888', fontSize: '11px', marginLeft: '6px'}}>
                                  (currently part of {allOrgsData.find(p => p.id === o.parentOrg)?.name || o.parentOrg})
                                </span>
                              )}
                            </button>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Sources */}
            {(org.sources && org.sources.length > 0) || isAdmin ? (
              <div className='detail-sidebar-section'>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <h3 className='detail-sidebar-heading'>Sources</h3>
                  {isAdmin && (
                    <button
                      className='admin-btn-small'
                      onClick={() => setShowSourcesEditor(true)}
                      title='Edit sources'
                      style={{padding: '0.25rem 0.5rem', fontSize: '0.85rem'}}
                    >
                      Edit
                    </button>
                  )}
                </div>
                <ul className='detail-sidebar-links'>
                  {(editedData.sources !== undefined ? editedData.sources : org.sources || []).map((source, idx) => (
                    <li key={idx}>
                      <a href={source.url} target='_blank' rel='noopener noreferrer'>
                        {source.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {showSourcesEditor && (
              <SourcesEditor
                sources={editedData.sources !== undefined ? editedData.sources : org.sources || []}
                onSave={handleSourcesSave}
                onClose={() => setShowSourcesEditor(false)}
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />
    </>
  );
}
